// Firebase Analytics › All Apps (BigQuery): every app stream in GA4 property
// 164367698 side by side. The Firebase pages above mix them into one number;
// this splits them by app and platform and adds installs/uninstalls, screens,
// events, versions and countries.

import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { COLORS, DataTable, DaysPicker, Section, StateBanner, Tile, chartTheme, fmtInt, fmtPct, useBqInsights } from "../../../components/bigquery/bqShared.jsx";

const PLATFORM = { IOS: "iOS", ANDROID: "Android" };

export default function AppsBigQuery() {
  const [days, setDays] = useState(30);
  const [trendMetric, setTrendMetric] = useState("active_users");
  const [focus, setFocus] = useState("barsys_ai");
  const { data, loading, error } = useBqInsights("apps", days);
  const t = chartTheme();

  const label = (app) => {
    const r = (data?.apps || []).find((a) => a.app === app);
    return r ? r.label : app;
  };
  const appKeys = useMemo(() => [...new Set((data?.apps || []).map((a) => a.app))], [data]);

  const trendChart = useMemo(() => {
    const rows = data?.trend || [];
    const dates = [...new Set(rows.map((r) => r.date))].sort();
    const top = appKeys.slice(0, 6);
    return {
      series: top.map((app) => {
        const m = new Map(rows.filter((r) => r.app === app).map((r) => [r.date, r[trendMetric]]));
        return { name: `${label(app)}${app.startsWith("com.") || app.startsWith("io.") ? ` (${app})` : ""}`, data: dates.map((d) => m.get(d) ?? 0) };
      }),
      options: {
        chart: { ...t.chart, type: "line" },
        colors: COLORS,
        stroke: { width: 2, curve: "smooth" },
        xaxis: { categories: dates, labels: { ...t.xaxisLabels, rotate: -45 }, tickAmount: Math.min(dates.length, 12) },
        yaxis: { labels: t.yaxisLabels, min: 0 },
        legend: t.legend, grid: t.grid, tooltip: t.tooltip, dataLabels: t.dataLabels,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, trendMetric, appKeys]);

  const tot = data?.totals || {};

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">FIREBASE ANALYTICS</li>
            <li className="breadcrumb-item active">ALL APPS (BIGQUERY)</li>
          </ul>
          <h1 className="page-header mb-0">
            All Apps <small>every Barsys app stream, from BigQuery</small>
          </h1>
          <div className="small text-inverse text-opacity-50 mt-1">Source: GA4 BigQuery export (property 164367698), {tot.apps || 0} app streams</div>
        </div>
        <div className="ms-auto mt-2 mt-md-0">
          <DaysPicker days={days} setDays={setDays} disabled={loading} />
        </div>
      </div>
      <StateBanner loading={loading} error={error} hasData={!!data} />

      {data && (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          <div className="row">
            <Tile label="Active app users" value={fmtInt(tot.active_users)} sub={`last ${data.window_days} days, all apps`} />
            <Tile label="Barsys AI share" value={fmtPct(tot.ai_app_share_pct)} sub="of active app users" />
            <Tile label="Sessions" value={fmtInt(tot.sessions)} />
            <Tile label="Installs" value={fmtInt(tot.installs)} sub="first opens" />
            <Tile label="Uninstalls (Android)" value={fmtInt(tot.uninstalls_android)} sub="iOS not reported" />
            <Tile label="App streams" value={fmtInt(tot.apps)} />
          </div>

          <Section title="APPS" subtitle={`last ${data.window_days} days, per app and platform`} bodyClass="p-0">
            <DataTable
              rows={data.apps}
              columns={[
                { key: "label", label: "App", render: (r) => <span title={r.bundle_id}>{r.label} <span className="text-inverse text-opacity-50 small">{r.bundle_id}</span></span>, width: 360 },
                { key: "platform", label: "Platform", render: (r) => PLATFORM[r.platform] || r.platform },
                { key: "active_users", label: "Active users", num: true },
                { key: "wau", label: "WAU", num: true },
                { key: "dau", label: "DAU", num: true },
                { key: "stickiness_pct", label: "DAU/active", num: true, fmt: fmtPct },
                { key: "sessions", label: "Sessions", num: true },
                { key: "sessions_per_user", label: "Sess./user", num: true, fmt: (v) => v ?? "—" },
                { key: "installs", label: "Installs", num: true },
                { key: "uninstalls", label: "Uninstalls", num: true },
                { key: "net_installs", label: "Net", num: true, render: (r) => (r.net_installs === null ? "—" : <span className={r.net_installs < 0 ? "text-danger" : "text-success"}>{fmtInt(r.net_installs)}</span>) },
                { key: "updates", label: "Updates", num: true },
                { key: "signed_in_pct", label: "Signed in", num: true, fmt: fmtPct },
              ]}
            />
          </Section>

          <Section
            title="DAILY TREND"
            subtitle="top 6 app streams"
            right={
              <select className="form-select form-select-sm w-auto" value={trendMetric} onChange={(e) => setTrendMetric(e.target.value)}>
                <option value="active_users">Active users</option>
                <option value="installs">Installs</option>
                <option value="uninstalls">Uninstalls (Android)</option>
              </select>
            }
          >
            <Chart type="line" height={300} series={trendChart.series} options={trendChart.options} />
          </Section>

          <Section
            title="APP DETAIL"
            right={
              <select className="form-select form-select-sm w-auto" value={focus} onChange={(e) => setFocus(e.target.value)}>
                {appKeys.map((a) => <option key={a} value={a}>{label(a)} ({a})</option>)}
              </select>
            }
            bodyClass="p-0"
          >
            <div className="row g-0">
              <div className="col-lg-4 border-end">
                <div className="fw-bold small px-3 pt-3">Top screens</div>
                <DataTable rows={data.top_screens?.[focus]} columns={[{ key: "screen", label: "Screen", width: 220 }, { key: "views", label: "Views", num: true }, { key: "users", label: "Users", num: true }]} empty="No screen views reported" />
              </div>
              <div className="col-lg-4 border-end">
                <div className="fw-bold small px-3 pt-3">Top events</div>
                <DataTable rows={data.top_events?.[focus]} columns={[{ key: "event", label: "Event", width: 220 }, { key: "count", label: "Count", num: true }, { key: "users", label: "Users", num: true }]} />
              </div>
              <div className="col-lg-4">
                <div className="fw-bold small px-3 pt-3">Versions</div>
                <DataTable rows={data.versions?.[focus]} columns={[{ key: "platform", label: "Platform", render: (r) => PLATFORM[r.platform] || r.platform }, { key: "version", label: "Version" }, { key: "users", label: "Users", num: true }]} />
                <div className="fw-bold small px-3 pt-3">Countries</div>
                <DataTable rows={data.countries?.[focus]} columns={[{ key: "country", label: "Country" }, { key: "users", label: "Users", num: true }]} />
              </div>
            </div>
          </Section>

          <div className="small text-inverse text-opacity-50 mb-4">
            {data.notes?.uninstalls} {data.notes?.users}
          </div>
        </div>
      )}
    </div>
  );
}
